/**
 * Formatting for the billing amounts shown on the account page.
 *
 * Pure and separate from the directive so it can be tested directly — the numbers here
 * are the ones a customer checks their bank statement against.
 */

/**
 * Render a billing amount the way the card shows it, e.g. `CA$13.00 CAD`.
 *
 * The amount is a whole invoice total, not a per-seat rate: the server reads it off the
 * invoice Stripe will issue, which already covers every seat on the subscription. It
 * must not be multiplied by the quantity — doing so used to overstate a multi-seat
 * renewal by a factor of the seat count (issue #547).
 *
 * Returns an empty string only when the next charge is genuinely unknown, so a caller
 * can hide the line rather than guess at a figure. A zero charge is a known answer, not
 * a missing one: a fully discounted renewal is worth stating.
 */
export function formatBillingAmount(amount: number | null | undefined, currency: string | null | undefined): string {

    if (amount === null || amount === undefined || !currency) {
        return "";
    }

    try {

        const formattedNumber = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'symbol',
        }).format(amount)

        return `${formattedNumber} ${currency.toUpperCase()}`;

    } catch {
        return `$${amount} ${currency.toUpperCase()}`;
    }
}
